import {
  AloftPrice,
  CraftsmanCostProps,
  getCraftsmanCost,
  isDefined,
  isUndefined,
  ORDER_FEATURES,
  schPositiveInt,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementEntityIds, selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateBelowGradeHalfBathCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (isUndefined(improvementsForTransaction) || isUndefined(identification)) {
    return undefined;
  }

  const {
    basement,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = improvementsForTransaction;
  const {
    address: { PostalCode: zipCode },
  } = identification;

  const halfBaths = basement?.halfBaths ?? schPositiveInt.parse(0);

  if (isUndefined(quality) || (quality * 10) % 1 !== 0 || quality < 1 || isUndefined(zipCode) || !ORDER_FEATURES.basement.hasFeature(improvementsForTransaction)) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getHalfBathroomsCost({
    halfBaths,
    quality,
    propertyType,
    zipCode,
  });
};

const getTotalOrBreakdownBelowGradeHalfBathCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculateBelowGradeHalfBathCost(
    costProps,
    improvements,
    propertyIdentification,
    id,
  );

  if (costOutput === undefined) {
    return AloftPrice.zero;
  }

  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalBelowGradeHalfBathCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) =>
  getTotalOrBreakdownBelowGradeHalfBathCost(
    costProps,
    improvements,
    propertyIdentification,
    id,
    'totalCost',
  );

export const selectBelowGradeHalfBathCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getTotalBelowGradeHalfBathCost,
);

export const selectZipOnlyBelowGradeHalfBathCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownBelowGradeHalfBathCost(
      costProps,
      improvements,
      propertyIdentification,
      id,
      'zipOnly',
    ),
);

export const selectBelowGradeHalfBathCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectImprovementEntityIds,
  ],
  (costProps, improvements, propertyIdentification, improvementEntityIds) => improvementEntityIds.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalBelowGradeHalfBathCost(
        costProps,
        improvements,
        propertyIdentification,
        propID,
      );
      if (isDefined(cost)) acc[propID] = cost;
      return acc;
    }, {}),
);
